// @ts-nocheck
import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface Hashtag {
  id: string;
  tag: string;
  post_count: number;
}

// Extract hashtags from text content
export const parseHashtags = (content: string): string[] => {
  const hashtagRegex = /#([a-zA-Z0-9_]+)/g;
  const matches = content.match(hashtagRegex);
  if (!matches) return [];
  
  // Return unique, lowercase tags without the # symbol
  return [...new Set(matches.map(tag => tag.slice(1).toLowerCase()))];
};

// Save hashtags for a post
export const saveHashtags = async (
  tags: string[],
  postId: string
): Promise<void> => {
  if (tags.length === 0) return;

  // For each tag, upsert to wall_hashtags and link to post
  for (const tag of tags) {
    // Upsert hashtag (create if not exists)
    const { data: existingTag, error: fetchError } = await supabase
      .from('wall_hashtags')
      .select('id')
      .eq('tag', tag)
      .maybeSingle();

    let hashtagId: string;

    if (existingTag) {
      hashtagId = existingTag.id;
    } else {
      // Create new hashtag
      const { data: newTag, error: insertError } = await supabase
        .from('wall_hashtags')
        .insert({ tag })
        .select('id')
        .single();

      if (insertError || !newTag) {
        console.error('Failed to create hashtag:', tag, insertError);
        continue;
      }
      hashtagId = newTag.id;
    }

    // Link hashtag to post (ignore if already exists)
    await supabase
      .from('wall_post_hashtags')
      .upsert({
        post_id: postId,
        hashtag_id: hashtagId,
      }, { onConflict: 'post_id,hashtag_id' });
  }

  // Update the hashtags JSONB on the post for quick access
  await supabase
    .from('wall_posts')
    .update({ hashtags: tags })
    .eq('id', postId);
};

export const useHashtagSearch = () => {
  const [results, setResults] = useState<Hashtag[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const search = useCallback(async (query: string) => {
    if (!query || query.length < 1) {
      setResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const { data, error } = await supabase
        .from('wall_hashtags')
        .select('id, tag, post_count')
        .ilike('tag', `${query.toLowerCase()}%`)
        .order('post_count', { ascending: false })
        .limit(10);

      if (error) throw error;
      setResults(data || []);
    } catch (err) {
      console.error('Error searching hashtags:', err);
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  const clearResults = useCallback(() => {
    setResults([]);
  }, []);

  return {
    results,
    isSearching,
    search,
    clearResults,
  };
};

// Hook to get trending hashtags
export const useTrendingHashtags = () => {
  const [hashtags, setHashtags] = useState<Hashtag[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchTrending = useCallback(async () => {
    setIsLoading(true);
    try {
      // Get hashtags with recent post activity (last 7 days)
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

      const { data, error } = await supabase
        .from('wall_post_hashtags')
        .select(`
          hashtag_id,
          wall_hashtags!inner (
            id,
            tag,
            post_count
          )
        `)
        .gte('created_at', sevenDaysAgo.toISOString());

      if (error) throw error;

      // Count occurrences in last 7 days
      const countMap: Record<string, { tag: Hashtag; count: number }> = {};
      data?.forEach((item: any) => {
        const hashtag = item.wall_hashtags;
        if (hashtag) {
          if (!countMap[hashtag.id]) {
            countMap[hashtag.id] = { tag: hashtag, count: 0 };
          }
          countMap[hashtag.id].count++;
        }
      });

      // Sort by recent activity and take top 5
      const trending = Object.values(countMap)
        .sort((a, b) => b.count - a.count)
        .slice(0, 5)
        .map(item => ({
          ...item.tag,
          recentCount: item.count,
        }));

      setHashtags(trending);
    } catch (err) {
      console.error('Error fetching trending hashtags:', err);
      setHashtags([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    hashtags,
    isLoading,
    fetchTrending,
  };
};
